import { BotId, NlpChatQuery, QueryListFilter } from '@feedyou-ai/feedbot-schema'

import { serverApi, ServerApiTag } from '../../store/serverApi'

export type QueryReturnValue = {
  queries: NlpChatQuery[]
  continuationToken?: string
}

export type QueryArgs = {
  botId: BotId
  queryFilter?: QueryListFilter
  continuationToken?: string
}

export const nlpChatQueriesApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getNlpChatQueries: builder.query<QueryReturnValue, QueryArgs>({
      query: ({ botId, queryFilter, continuationToken }) => ({
        url: `/bots/${botId}/nlp/queries`,
        params: { ...queryFilter, continuationToken }
      }),
      providesTags: [ServerApiTag.NLP_CHAT_QUERY_LIST]
    }),
    rateNlpChatQuery: builder.mutation<
      void,
      {
        botId: BotId
        day: string
        queryId: string
        rating: boolean
        role: 'user' | 'customer' | 'admin'
        internalRatingExplanation: string
      }
    >({
      query: ({ botId, queryId, day, role, rating, internalRatingExplanation }) => {
        return {
          method: 'PATCH',
          url: `/bots/${botId}/nlp/query/${day}/${queryId}/rate`,
          body: { day, rating, role, internalRatingExplanation },
          responseHandler: () => Promise.resolve(undefined)
        }
      },
      async onQueryStarted(
        { queryId, rating, role, internalRatingExplanation },
        { dispatch, queryFulfilled, getState }
      ) {
        try {
          await queryFulfilled

          const ratingValue = rating ? 1 : -1
          const ratingPatch =
            role === 'admin'
              ? {
                  internalRating: ratingValue,
                  adminRating: ratingValue,
                  explanation: internalRatingExplanation,
                  adminRatingExplanation: internalRatingExplanation
                }
              : {
                  customerRating: ratingValue,
                  customerRatingExplanation: internalRatingExplanation
                }

          const entriesToUpdate = serverApi.util.selectInvalidatedBy(getState(), [
            ServerApiTag.KNOWLEDGE_BASE_QUERY,
            ServerApiTag.NLP_CHAT_QUERY_LIST
          ])

          for (const { endpointName, originalArgs } of entriesToUpdate) {
            dispatch(
              (serverApi.util.updateQueryData as any)(endpointName, originalArgs, (draft: any) => {
                const query = draft.queries?.find((q: any) => q.queryId === queryId)
                if (query) Object.assign(query, ratingPatch)
              })
            )
          }
        } catch {
          // Mutation failed - RatingControls will revert to its pre-loading state
        }
      },
      invalidatesTags: [ServerApiTag.NLP_CHAT_QUERY_LIST]
    })
  })
})
